import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TopMenuShell from '../dashboard/TopMenuShell';
import { SearchBar } from '../ui/SearchBar';
import { Pagination } from '../ui/Pagination';
import { GlassCard } from '../ui/GlassCard';
import { MultiSelectFilter } from '../ui/MultiSelectFilter';
import { ErrorMessage } from '../ui/ErrorMessage';
import EventCard from './EventCard';
import { Event, EventStats } from '../../types/Event';
import {
  getUpcomingEvents,
  calculateEventStats,
  filterEventsByCity,
  searchEvents,
} from '../../services/eventService';

const ITEMS_PER_PAGE = 10;

const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters & search
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Load events on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getUpcomingEvents();
        if (!cancelled) {
          setEvents(data);
          setError(null);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message ?? 'Failed to load events');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Stats computed from all events (before filtering)
  const stats: EventStats = useMemo(() => calculateEventStats(events), [events]);

  // Apply city filter then search
  const filteredEvents = useMemo(() => {
    let result = filterEventsByCity(events, selectedCities);
    result = searchEvents(result, searchQuery);
    return result;
  }, [events, selectedCities, searchQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / ITEMS_PER_PAGE));
  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredEvents.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredEvents, currentPage]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [selectedCities, searchQuery]);

  const handleCityChange = useCallback((cities: string[]) => {
    setSelectedCities(cities);
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  return (
    <div className="min-h-screen">
      <TopMenuShell>
        {/* Stats cards + city filter */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
              <GlassCard style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white' }}>
                  {stats.totalEvents}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                  Total Events
                </div>
              </GlassCard>

              <GlassCard style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#bbf7d0' }}>
                  {stats.thisWeek}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                  Events this Week
                </div>
              </GlassCard>

              <GlassCard style={{ padding: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f9d0ff' }}>
                  {stats.thisMonth}
                </div>
                <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                  Events this Month
                </div>
              </GlassCard>
            </div>
          </div>

          {/* City filter */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <MultiSelectFilter
              label="City"
              options={stats.cities}
              selectedValues={selectedCities}
              onChange={handleCityChange}
              placeholder="All Cities"
            />

            {selectedCities.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  onClick={() => setSelectedCities([])}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Search bar */}
        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
          <SearchBar
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search companies, sectors, contacts, or locations..."
            aria-label="Search events"
          />
        </div>
      </TopMenuShell>

      {/* Content area */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e5e7eb',
              borderTopColor: '#0ea5e9',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ fontSize: '16px' }}>Loading events...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {error && (
          <ErrorMessage message={`Failed to load events: ${error}`} />
        )}

        {!loading && !error && filteredEvents.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
            <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ margin: '0 auto 12px', opacity: 0.5 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p style={{ fontSize: '16px' }}>
              {events.length === 0
                ? 'No upcoming events found yet. Events are refreshed weekly.'
                : 'No events match your filters.'}
            </p>
          </div>
        )}

        {!loading && !error && paginatedEvents.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {paginatedEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && filteredEvents.length > ITEMS_PER_PAGE && (
          <div style={{ marginTop: '24px' }}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={filteredEvents.length}
              itemsPerPage={ITEMS_PER_PAGE}
              itemLabel="events"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
